import {
  WORK_ITEM_COMMENT_MAX, WORK_ITEM_DESCRIPTION_MAX, WORK_ITEM_PRIORITIES, WORK_ITEM_PROJECT_MAX,
  WORK_ITEM_STATUSES, WORK_ITEM_TITLE_MAX, type WorkItemCommentView, type WorkItemDetailView,
  type WorkItemPriority, type WorkItemStatus,
} from '@hilo/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type CSSProperties, type FormEvent, type RefObject, useEffect, useId, useRef, useState } from 'react';
import { ApiError } from '../../lib/api';
import { errorText } from '../../lib/copy';
import { useCurrentUser } from '../../lib/session';
import {
  addWorkComment, canDeleteComment, canDeleteWork, canManageWork, canMoveWork, deleteWorkComment,
  deleteWorkItem, dueTone, fetchAssignees, fetchProjects, fetchWorkItem, formatDay, isRevisionConflict,
  PRIORITY_LABEL, refreshWork, STATUS_LABEL, todayIn, updateWorkItem, workKeys, type WorkItemChanges,
} from '../../lib/workItems';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { DatePicker } from '../../ui/DatePicker';
import { hueOf, initialsOf } from '../../ui/Avatar';
import {
  IconBack, IconCalendar, IconFlag, IconFolder, IconMegaphone, IconPerson, IconProgress, IconSend, IconTick, IconTrash,
} from '../../ui/icons';
import { Select } from '../../ui/Select';
import { formatAgo } from '../../lib/time';
import { formatDateTime } from '../connections/ConnectionsPage';
import styles from './WorkItemPanel.module.css';

const conflictText = 'Alguien cambió esta tarea mientras la editabas. Revisa la versión nueva y vuelve a intentarlo.';

function fieldsOf(item: WorkItemDetailView) {
  return {
    title: item.title, description: item.description, status: item.status, priority: item.priority,
    project: item.project ?? '', assigneeId: item.assignee?.id ?? '',
    dueDate: item.dueDate ?? '', publishDate: item.publishDate ?? '',
  };
}

function ItemContent({ item, titleId, titleRef, onClose, onDirtyChange }: {
  item: WorkItemDetailView; titleId: string; titleRef: RefObject<HTMLHeadingElement | null>; onClose: () => void; onDirtyChange?: (dirty: boolean) => void;
}) {
  const user = useCurrentUser();
  const client = useQueryClient();
  const manage = canManageWork(user.role);
  const move = canMoveWork(user, item);
  const assignees = useQuery({ queryKey: workKeys.assignees, queryFn: fetchAssignees, enabled: manage });
  const projects = useQuery({ queryKey: workKeys.projects, queryFn: fetchProjects, enabled: manage });
  const prefix = useId();
  const commentsId = useId();
  const commentId = useId();
  const formId = useId();
  const [editor, setEditor] = useState(() => ({ base: item, fields: fieldsOf(item) }));
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const working = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [deleting, setDeleting] = useState<'item' | WorkItemCommentView | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const { base, fields } = editor;
  const changes: WorkItemChanges = {};
  if (manage) {
    if (fields.title.trim() !== base.title) changes.title = fields.title.trim();
    if (fields.description !== base.description) changes.description = fields.description;
    if (fields.priority !== base.priority) changes.priority = fields.priority;
    if ((fields.project.trim() || null) !== base.project) changes.project = fields.project.trim() || null;
    if ((fields.assigneeId || null) !== (base.assignee?.id ?? null)) changes.assigneeId = fields.assigneeId || null;
    if ((fields.dueDate || null) !== base.dueDate) changes.dueDate = fields.dueDate || null;
    if ((fields.publishDate || null) !== base.publishDate) changes.publishDate = fields.publishDate || null;
  }
  if (move && fields.status !== base.status) changes.status = fields.status;
  const changed = Object.keys(changes).length > 0;
  // El tablero pregunta antes de cerrar si hay campos sin guardar o un comentario a medio escribir.
  const dirty = changed || comment.trim() !== '';
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  useEffect(() => {
    if (!saved) return undefined;
    const timer = window.setTimeout(() => setSaved(false), 2500);
    return () => window.clearTimeout(timer);
  }, [saved]);

  // Refrescar comentarios o recibir avisos no descarta una edición ni adelanta su revisión.
  if (item !== base && !changed && !busy) setEditor({ base: item, fields: fieldsOf(item) });

  function edit<K extends keyof typeof fields>(key: K, value: typeof fields[K]) {
    setEditor((current) => ({ ...current, fields: { ...current.fields, [key]: value } }));
    setSaved(false);
  }

  function start() {
    if (working.current) return false;
    working.current = true;
    setBusy(true);
    setError(null);
    setSaved(false);
    return true;
  }

  function finish() {
    working.current = false;
    setBusy(false);
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!changed || (manage && !fields.title.trim()) || !start()) return;
    try {
      const updated = await updateWorkItem(item.id, base.revision, changes);
      setEditor({ base: updated, fields: fieldsOf(updated) });
      client.setQueryData(workKeys.detail(item.id), updated);
      await refreshWork(client);
      setSaved(true);
    } catch (failure) {
      if (isRevisionConflict(failure)) {
        setError(conflictText);
        try {
          const updated = await fetchWorkItem(item.id);
          client.setQueryData(workKeys.detail(item.id), updated);
          setEditor({ base: updated, fields: fieldsOf(updated) });
          await refreshWork(client);
        } catch (reloadFailure) {
          setError(`${conflictText} ${errorText(reloadFailure)}`);
        }
      } else setError(errorText(failure));
    } finally {
      finish();
    }
  }

  async function refreshDetail() {
    await client.refetchQueries({ queryKey: workKeys.detail(item.id), exact: true });
    await refreshWork(client);
  }

  async function sendComment(event?: FormEvent) {
    event?.preventDefault();
    if (!comment.trim() || !start()) return;
    try {
      await addWorkComment(item.id, comment.trim());
      setComment('');
      await refreshDetail();
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      finish();
    }
  }

  async function remove() {
    if (!deleting || !start()) return;
    setDeleteError(null);
    try {
      if (deleting === 'item') {
        await deleteWorkItem(item.id);
        await refreshWork(client);
        onDirtyChange?.(false);
        onClose();
      } else {
        await deleteWorkComment(item.id, deleting.id);
        await refreshDetail();
      }
      setDeleting(null);
    } catch (failure) {
      setDeleteError(errorText(failure));
    } finally {
      finish();
    }
  }

  const today = todayIn(user.timezone);
  const tone = dueTone(item, today);
  const people = assignees.data ?? [];
  const assigned = item.assignee;
  const options = [
    { value: '', label: 'Sin responsable' },
    ...people.map((person) => ({ value: person.id, label: person.displayName })),
    ...(assigned && !people.some((person) => person.id === assigned.id)
      ? [{ value: assigned.id, label: assigned.displayName }] : []),
  ];
  const owner = manage ? (people.find((person) => person.id === fields.assigneeId) ?? (fields.assigneeId === assigned?.id ? assigned : null)) : assigned;
  const toneLabel = tone === 'overdue' ? 'Vencida' : tone === 'today' ? 'Hoy' : tone === 'soon' ? 'Pronto' : null;
  const comments = [...item.comments].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const nearLimit = comment.length > WORK_ITEM_COMMENT_MAX * 0.9;

  function discard() {
    setEditor({ base, fields: fieldsOf(base) });
    setError(null);
  }

  return (
    <>
      <div className={styles.heading}>
      {manage ? (
        <>
          <h2 id={titleId} ref={titleRef} tabIndex={-1} className="visually-hidden">{item.title}</h2>
          <textarea className={styles.titleInput} aria-label="Título" required rows={1} maxLength={WORK_ITEM_TITLE_MAX} value={fields.title}
            disabled={busy} placeholder="Título de la tarea" onChange={(event) => edit('title', event.target.value.replace(/\n/g, ' '))}
            onKeyDown={(event) => { if (event.key === 'Enter') event.preventDefault(); }} />
        </>
      ) : <h2 id={titleId} ref={titleRef} tabIndex={-1} className={styles.title}>{item.title}</h2>}

      <p className={styles.byline}>
        Creada por {item.createdBy.displayName}
        {item.status === 'DONE' && item.completedAt && <> · completada <time dateTime={item.completedAt}>{formatDateTime(item.completedAt, user.timezone)}</time></>}
      </p>
      </div>

      <form id={formId} className={styles.form} onSubmit={(event) => void save(event)}>
        <dl className={styles.props}>
          <div className={styles.prop} data-status={fields.status}>
            <dt><IconProgress />Estado</dt>
            <dd>
              <span className={styles.statusDot} aria-hidden="true" />
              {move ? <Select variant="ghost" hideLabel label="Estado" value={fields.status}
                options={WORK_ITEM_STATUSES.map((value) => ({ value, label: STATUS_LABEL[value] }))}
                disabled={busy} onChange={(value: WorkItemStatus) => edit('status', value)} />
                : <span className={styles.value}>{STATUS_LABEL[item.status]}</span>}
            </dd>
          </div>
          <div className={styles.prop} data-priority={fields.priority}>
            <dt><IconFlag />Prioridad</dt>
            <dd>
              {manage ? <Select variant="ghost" hideLabel label="Prioridad" value={fields.priority}
                options={WORK_ITEM_PRIORITIES.map((value) => ({ value, label: PRIORITY_LABEL[value] }))}
                disabled={busy} onChange={(value: WorkItemPriority) => edit('priority', value)} />
                : <span className={styles.value}>{PRIORITY_LABEL[item.priority]}</span>}
            </dd>
          </div>
          <div className={styles.prop}>
            <dt><IconPerson />Responsable</dt>
            <dd>
              {owner
                ? <span className={styles.face} style={{ '--hue': hueOf(owner.id) } as CSSProperties} aria-hidden="true">{initialsOf(owner.displayName)}</span>
                : <span className={`${styles.face} ${styles.faceEmpty}`} aria-hidden="true" />}
              {manage ? <Select variant="ghost" hideLabel label="Responsable" value={fields.assigneeId} options={options}
                disabled={busy || assignees.isPending || assignees.isError} onChange={(value) => edit('assigneeId', value)} />
                : <span className={styles.value}>{item.assignee?.displayName ?? 'Sin responsable'}</span>}
            </dd>
          </div>
          <div className={styles.prop}>
            <dt><label htmlFor={`${prefix}-project`}><IconFolder />Proyecto</label></dt>
            <dd>
              {manage ? (
                <>
                  <input id={`${prefix}-project`} className={styles.ghostInput} list={`${prefix}-projects`} maxLength={WORK_ITEM_PROJECT_MAX}
                    autoComplete="off" placeholder="Sin proyecto" value={fields.project} disabled={busy} onChange={(event) => edit('project', event.target.value)} />
                  <datalist id={`${prefix}-projects`}>{projects.data?.map((project) => <option key={project} value={project} />)}</datalist>
                </>
              ) : <span className={styles.value}>{item.project ?? 'Sin proyecto'}</span>}
            </dd>
          </div>
          <div className={styles.prop}>
            <dt><label htmlFor={`${prefix}-due`}><IconCalendar />Entrega</label></dt>
            <dd>
              {manage
                ? <DatePicker id={`${prefix}-due`} label="Fecha de entrega" today={today} value={fields.dueDate} disabled={busy} onChange={(value) => edit('dueDate', value)} />
                : <span className={styles.value}>{item.dueDate ? formatDay(item.dueDate, today) : 'Sin fecha'}</span>}
              {toneLabel && <span className={styles.tone} data-tone={tone}>{toneLabel}</span>}
            </dd>
          </div>
          <div className={styles.prop}>
            <dt><label htmlFor={`${prefix}-publish`}><IconMegaphone />Publicación</label></dt>
            <dd>
              {manage
                ? <DatePicker id={`${prefix}-publish`} label="Fecha de publicación" today={today} value={fields.publishDate} disabled={busy} onChange={(value) => edit('publishDate', value)} />
                : <span className={styles.value}>{item.publishDate ? formatDay(item.publishDate, today) : 'Sin fecha'}</span>}
            </dd>
          </div>
        </dl>
        {projects.isError && <p className={styles.error} role="alert">No se pudieron cargar las sugerencias de proyectos. {errorText(projects.error)}</p>}
        {assignees.isError && <div className={styles.feedback}><p className={styles.error} role="alert">No se pudieron cargar los responsables. {errorText(assignees.error)}</p>
          <button type="button" className="btn btn-secondary" onClick={() => void assignees.refetch()}>Reintentar</button></div>}

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{manage ? <label htmlFor={`${prefix}-description`}>Descripción</label> : 'Descripción'}</h3>
          {manage
            ? <textarea id={`${prefix}-description`} className={styles.description} rows={3} maxLength={WORK_ITEM_DESCRIPTION_MAX}
              placeholder="Qué hay que hacer, enlaces, referencias…" value={fields.description} disabled={busy} onChange={(event) => edit('description', event.target.value)} />
            : <p className={item.description ? styles.descriptionText : styles.empty}>{item.description || 'Sin descripción'}</p>}
        </div>
      </form>
      {error && <p className={styles.error} role="alert">{error}</p>}

      <section className={styles.section} aria-labelledby={commentsId}>
        <h3 id={commentsId} className={styles.sectionTitle}>
          Comentarios{comments.length > 0 && <span className={styles.count}>{comments.length}</span>}
        </h3>
        {comments.length === 0
          ? <p className={styles.empty}>Sin comentarios todavía. Escribe el primero para dejar una nota al equipo.</p>
          : (
            <ol className={styles.commentList}>
              {comments.map((entry) => (
                <li key={entry.id} className={styles.comment}>
                  <span className={styles.face} style={{ '--hue': hueOf(entry.author.id) } as CSSProperties} aria-hidden="true">{initialsOf(entry.author.displayName)}</span>
                  <div className={styles.commentMain}>
                    <div className={styles.commentHead}>
                      <strong>{entry.author.id === user.id ? 'Tú' : entry.author.displayName}</strong>
                      <time dateTime={entry.createdAt} title={formatDateTime(entry.createdAt, user.timezone)}>{formatAgo(entry.createdAt, user.timezone)}</time>
                      {canDeleteComment(user, entry) && (
                        <button type="button" className={styles.commentDelete} disabled={busy}
                          aria-label={`Borrar comentario de ${entry.author.displayName}`} title="Borrar comentario"
                          onClick={() => { setDeleteError(null); setDeleting(entry); }}><IconTrash /></button>
                      )}
                    </div>
                    <p className={styles.commentBody}>{entry.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        <form className={styles.composer} onSubmit={(event) => void sendComment(event)}>
          <span className={styles.face} style={{ '--hue': hueOf(user.id) } as CSSProperties} aria-hidden="true">{initialsOf(user.displayName)}</span>
          <div className={styles.composerBox}>
            <label className="visually-hidden" htmlFor={commentId}>Escribir un comentario</label>
            <textarea id={commentId} className={styles.composerInput} rows={1} maxLength={WORK_ITEM_COMMENT_MAX} value={comment}
              placeholder="Escribe un comentario…" aria-describedby={`${commentId}-hint`} disabled={busy} onChange={(event) => setComment(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && !event.nativeEvent.isComposing) {
                  event.preventDefault(); void sendComment();
                }
              }} />
            <div className={styles.composerBar}>
              <span id={`${commentId}-hint`} className={styles.composerHint}>
                {nearLimit ? `${comment.length}/${WORK_ITEM_COMMENT_MAX}` : <><kbd>⌘</kbd> <kbd>Enter</kbd> para enviar</>}
              </span>
              <button type="submit" className={`btn btn-primary ${styles.send}`} disabled={busy || !comment.trim()}><IconSend />Comentar</button>
            </div>
          </div>
        </form>
      </section>

      {canDeleteWork(user.role) && <div className={styles.deleteArea}>
        <button type="button" className={styles.deleteTask} disabled={busy}
          onClick={() => { setDeleteError(null); setDeleting('item'); }}><IconTrash />Eliminar tarea</button>
      </div>}

      {(manage || move) && (changed || saved) && (
        <div className={styles.saveBar} role="status" data-saved={!changed || undefined}>
          {changed ? (
            <>
              <span className={styles.saveText}>Cambios sin guardar</span>
              <button type="button" className="btn btn-quiet" disabled={busy} onClick={discard}>Descartar</button>
              <button type="submit" form={formId} className="btn btn-primary" disabled={busy || (manage && !fields.title.trim())}>
                {busy ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </>
          ) : <span className={styles.saveText}><IconTick />Cambios guardados</span>}
        </div>
      )}

      <ConfirmDialog open={deleting !== null} title={deleting === 'item' ? '¿Eliminar esta tarea?' : '¿Borrar este comentario?'}
        confirmLabel={deleting === 'item' ? 'Eliminar tarea' : 'Borrar comentario'} busyLabel="Eliminando…" busy={busy}
        onConfirm={() => void remove()} onCancel={() => setDeleting(null)}>
        <p>{deleting === 'item' ? 'Se eliminarán la tarea y sus comentarios. Esta acción no se puede deshacer.' : 'El comentario se eliminará de la tarea. Esta acción no se puede deshacer.'}</p>
        {deleteError && <p className={styles.error} role="alert">{deleteError}</p>}
      </ConfirmDialog>
    </>
  );
}

export function WorkItemPanel({ id, onClose, onDirtyChange }: { id: string; onClose: () => void; onDirtyChange?: (dirty: boolean) => void }) {
  const detail = useQuery({ queryKey: workKeys.detail(id), queryFn: () => fetchWorkItem(id) });
  const titleId = useId();
  const title = useRef<HTMLHeadingElement>(null);
  const panel = useRef<HTMLElement>(null);
  const close = useRef(onClose);
  useEffect(() => { close.current = onClose; }, [onClose]);

  useEffect(() => {
    const previous = document.activeElement;
    function onKey(event: KeyboardEvent) {
      if (event.defaultPrevented || document.querySelector('dialog[open]')) return;
      if (event.key === 'Escape') { event.preventDefault(); close.current(); }
      // La hoja móvil ocupa toda la pantalla: el teclado se queda en sus controles visibles.
      if (event.key !== 'Tab' || !window.matchMedia('(max-width: 63.99rem)').matches) return;
      const controls = Array.from(panel.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), textarea:not(:disabled), [tabindex="0"]') ?? [])
        .filter((element) => element.getClientRects().length > 0);
      const first = controls[0];
      const last = controls.at(-1);
      if (event.shiftKey && (document.activeElement === first || document.activeElement === title.current)) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, [id]);

  // El foco va al título al abrir y otra vez cuando llega la tarea (el título se vuelve a montar).
  useEffect(() => { title.current?.focus({ preventScroll: true }); }, [id, detail.isSuccess]);

  const missing = detail.error instanceof ApiError && detail.error.status === 404;
  return (
    <aside ref={panel} className={styles.panel} aria-labelledby={titleId}>
      <header className={styles.header}>
        <button type="button" className={`btn btn-quiet ${styles.back}`} onClick={onClose}><IconBack />Volver al tablero</button>
        <span className={styles.crumb}>{detail.data?.project ?? 'Tarea'}</span>
        <button type="button" className={styles.close} aria-label="Cerrar detalle" onClick={onClose}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" focusable="false"><path d="m6 6 12 12M6 18 18 6" /></svg>
        </button>
      </header>
      <div className={styles.content}>
        {detail.isPending ? <><h2 id={titleId} ref={title} tabIndex={-1} className={styles.title}>Detalle de tarea</h2><p className={styles.hint} role="status">Cargando tarea…</p></>
          : detail.isError ? <div className={styles.feedback}>
            <h2 id={titleId} ref={title} tabIndex={-1} className={styles.title}>Detalle de tarea</h2>
            <p className={styles.error} role="alert">{missing ? 'Esta tarea ya no existe' : errorText(detail.error)}</p>
            <button type="button" className="btn btn-secondary" onClick={missing ? onClose : () => void detail.refetch()}>{missing ? 'Cerrar' : 'Reintentar'}</button>
          </div>
          : <ItemContent key={id} item={detail.data} titleId={titleId} titleRef={title} onClose={onClose} onDirtyChange={onDirtyChange} />}
      </div>
    </aside>
  );
}
